/**
 * /clientes — Lista de adquirentes con búsqueda + paginación.
 *
 * No descifra PII (performance). Para ver email/phone, abrir el detalle.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Plus, Loader2, Pencil } from 'lucide-react'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { customerApi } from '@/lib/api'
import { ROUTES } from '@/lib/constants'

export function CustomersListPage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  const list = useQuery({
    queryKey: ['customers', activeOrgId, search, showArchived],
    queryFn: () =>
      customerApi.list(activeOrgId!, {
        search: search || undefined,
        only_active: !showArchived,
        limit: 100,
      }),
    enabled: !!activeOrgId,
  })

  if (authLoading) return <AuthLoading />
  if (!activeOrgId) return <NoOrg />

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Clientes</h1>
            <p className="mt-1 text-sm text-fg-secondary">
              Adquirentes a quienes facturas. Datos de PII (email, teléfono) cifrados at-rest.
            </p>
          </div>
          <Link
            to={ROUTES.CUSTOMER_NEW}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" /> Nuevo cliente
          </Link>
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por razón social o NIT/CC..."
              className="w-full rounded-md border border-border bg-bg-app py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-fg-secondary">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Incluir archivados
          </label>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-bg-surface">
          {list.isLoading && (
            <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </div>
          )}
          {list.isError && (
            <div className="p-6 text-sm text-error-fg">
              Error: {(list.error as Error).message}
            </div>
          )}
          {list.data && list.data.items.length === 0 && (
            <div className="p-6 text-center text-sm text-fg-muted">
              {search ? 'Sin resultados.' : 'Sin clientes aún. Crea el primero.'}
            </div>
          )}
          {list.data && list.data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-bg-elevated text-left text-xs uppercase tracking-wide text-fg-muted">
                <tr>
                  <th className="px-4 py-2.5">Razón social</th>
                  <th className="px-4 py-2.5">Identificación</th>
                  <th className="px-4 py-2.5">Ciudad</th>
                  <th className="px-4 py-2.5">Estado</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.data.items.map((c) => (
                  <tr key={c.id} className="hover:bg-bg-elevated">
                    <td className="px-4 py-2.5">
                      <span className="font-medium">{c.legal_name}</span>
                      {c.trade_name && (
                        <span className="ml-2 text-xs text-fg-muted">({c.trade_name})</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">
                      {c.identification_type}-{c.identification_number}
                      {c.verification_digit && `-${c.verification_digit}`}
                    </td>
                    <td className="px-4 py-2.5 text-fg-secondary">
                      {c.city}, {c.department}
                    </td>
                    <td className="px-4 py-2.5">
                      {c.is_active ? (
                        <span className="rounded-full bg-success-tint px-2 py-0.5 text-xs text-success-fg">
                          Activo
                        </span>
                      ) : (
                        <span className="rounded-full bg-bg-elevated px-2 py-0.5 text-xs text-fg-muted">
                          Archivado
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Link
                        to={ROUTES.CUSTOMER_EDIT.replace(':id', c.id)}
                        className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Editar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {list.data && (
          <p className="mt-3 text-xs text-fg-muted">
            Mostrando {list.data.items.length} de {list.data.total} clientes.
          </p>
        )}
      </div>
    </AppShell>
  )
}

function AuthLoading() {
  return (
    <AppShell>
      <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
      </div>
    </AppShell>
  )
}

function NoOrg() {
  return (
    <AppShell>
      <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
        Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
      </div>
    </AppShell>
  )
}
